import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu, Home, Info, Mail, Search, LogIn, UserPlus, Star, TestTube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/use-site-settings";

const MobileNav: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user, loginAsTestUser } = useAuth();
  const { featuresTestEnabled } = useSiteSettings();

  const handleNavigation = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleTestLogin = async () => {
    setOpen(false);
    await toast.promise(
      loginAsTestUser(),
      {
        loading: "Setting up test account...",
        success: () => {
          navigate('/admin');
          return "Welcome to the demo! Redirecting...";
        },
        error: (error) => `Failed to access test account: ${error.error || 'Unknown error'}`,
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden hover:bg-primary/10"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 p-0">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b">
            <SheetTitle className="text-lg font-semibold">Navigation</SheetTitle>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            <nav className="space-y-2">
              <Button
                variant="ghost"
                className="w-full justify-start text-base h-12"
                onClick={() => handleNavigation("/")}
              >
                <Home className="h-4 w-4 mr-3" />
                Home
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-base h-12"
                onClick={() => handleNavigation("/features")}
              >
                <Star className="h-4 w-4 mr-3" />
                Features
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-base h-12"
                onClick={() => handleNavigation("/about")}
              >
                <Info className="h-4 w-4 mr-3" />
                About
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-base h-12"
                onClick={() => handleNavigation("/contact")}
              >
                <Mail className="h-4 w-4 mr-3" />
                Contact
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-base h-12"
                onClick={() => handleNavigation("/legal-lookup")}
              >
                <Search className="h-4 w-4 mr-3" />
                Legal Lookup
              </Button>

              <Separator className="my-4" />

              {!isAuthenticated && featuresTestEnabled && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-base h-12 mb-4"
                  onClick={handleTestLogin}
                >
                  <TestTube className="h-4 w-4 mr-3" />
                  Try Demo
                </Button>
              )}

              {isAuthenticated ? (
                <Button
                  className="w-full text-base h-12"
                  onClick={() => handleNavigation("/admin")}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button
                    className="w-full text-base h-12"
                    onClick={() => handleNavigation("/auth?tab=signup")}
                  >
                    <UserPlus className="h-4 w-4 mr-3" />
                    Create Account
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full text-base h-12"
                    onClick={() => handleNavigation("/auth")}
                  >
                    <LogIn className="h-4 w-4 mr-3" />
                    Sign In
                  </Button>
                </div>
              )}
            </nav>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;